import { useState } from "react";
import { track } from "@/lib/track";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, Gift } from "lucide-react";
import { motion } from "framer-motion";
import BrainLogo from "@/components/BrainLogo";
import GoogleButton from "@/components/GoogleButton";

const pwStrength = (pw) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length < 8) return { label: "Too short — use at least 8 characters", color: "#F87171", pct: 15 };
  if (score <= 2) return { label: "Weak — add numbers, symbols or mixed case", color: "#F59E0B", pct: 40 };
  if (score === 3) return { label: "Good", color: "#5AC8FA", pct: 70 };
  return { label: "Strong", color: "#34D399", pct: 100 };
};

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [referralCode, setReferralCode] = useState((params.get("ref") || "").trim().toUpperCase());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const f = e.currentTarget.elements;
    const email = f["signup-email"]?.value || "";
    const name = f["signup-name"]?.value || "";
    const password = f["signup-password"]?.value || "";
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the Terms & Privacy Policy and confirm you're 18+ (or have guardian consent).");
      return;
    }
    setLoading(true);
    try {
      await signup(email, password, name, referralCode, { accept_terms: true, age_confirmed: true });
      track("CompleteRegistration", "sign_up", { method: "email" });
      toast.success(referralCode ? "Account created — bonus gems added!" : "Account created.");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sign up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-5 mesh-bg">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px]"
      >
        <Link to="/" className="flex items-center justify-center gap-2 mb-12" data-testid="signup-logo-link">
          <BrainLogo size={32} />
          <span className="font-semibold tracking-tight text-[18px]">Studivault</span>
        </Link>

        <div className="text-center mb-10">
          <h1 className="display-3">Create your account</h1>
          <p className="text-[15px] text-[#B0AEBC] mt-2">Free forever for your first 5 topics.</p>
        </div>

        <form onSubmit={onSubmit} data-testid="signup-form" className="space-y-3">
          <div>
            <label htmlFor="signup-name" className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Name</label>
            <input
              id="signup-name"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="signup-name-input"
              className="input"
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="signup-email" className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Email</label>
            <input
              id="signup-email"
              autoComplete="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="signup-email-input"
              className="input"
              placeholder="you@email.com"
            />
          </div>
          <div>
            <label htmlFor="signup-password" className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Password</label>
            <input
              id="signup-password"
              autoComplete="new-password"
              aria-describedby="signup-password-hint"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="signup-password-input"
              className="input"
              placeholder="At least 8 characters"
            />
            <div className="h-1 rounded-full bg-white/10 mt-2 overflow-hidden" aria-hidden="true">
              <div className="h-full rounded-full transition-[width,background-color] duration-300"
                style={{ width: password ? `${pwStrength(password).pct}%` : "0%", background: pwStrength(password).color }} />
            </div>
            <p id="signup-password-hint" className="text-[11px] mt-1.5 min-h-[16px]" style={{ color: password ? pwStrength(password).color : "#8E8B99" }} data-testid="signup-password-strength">
              {password ? pwStrength(password).label : "Use 8+ characters with a mix of letters, numbers and symbols."}
            </p>
          </div>

          <div>
            <label htmlFor="signup-referral" className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block flex items-center gap-1.5">
              <Gift size={12} className="text-[#B8A4FF]" /> Referral code <span className="text-[#8E8B99] font-normal">(optional)</span>
            </label>
            <input
              id="signup-referral"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              data-testid="signup-referral-input"
              maxLength={6}
              className="input font-mono tracking-[0.2em] uppercase placeholder:tracking-normal placeholder:font-sans placeholder:normal-case"
              placeholder="Got a friend's code? +15 bonus gems"
            />
          </div>

          <label className="flex items-start gap-2.5 mt-5 cursor-pointer select-none" data-testid="signup-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                // Sync browser-autofilled values into state first so the re-render can't wipe them.
                const f = e.target.form;
                if (f) {
                  if (f.elements["signup-name"]?.value) setName(f.elements["signup-name"].value);
                  if (f.elements["signup-email"]?.value) setEmail(f.elements["signup-email"].value);
                  if (f.elements["signup-password"]?.value) setPassword(f.elements["signup-password"].value);
                }
                setConsent(e.target.checked);
              }}
              data-testid="signup-consent-checkbox"
              className="mt-0.5 w-4 h-4 accent-[#7C5CFF] shrink-0"
            />
            <span className="text-[12px] text-[#B0AEBC] leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="text-[#B8A4FF] hover:underline">Terms</Link> and{" "}
              <Link to="/privacy-policy" target="_blank" className="text-[#B8A4FF] hover:underline">Privacy Policy</Link>, and confirm I'm 18 or older (or have my parent/guardian's consent).
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !consent}
            data-testid="signup-submit-button"
            className="btn btn-primary w-full mt-5 py-3.5 text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating…" : <>Create account <ArrowRight size={15} className="inline ml-1 -mt-0.5" /></>}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[12px] text-[#8E8B99]">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <div className={consent ? "" : "opacity-50 pointer-events-none"} data-testid="signup-google-gate" aria-disabled={!consent}>
          <GoogleButton label="Sign up with Google" />
        </div>
        <p className={`text-center text-[11px] text-[#8E8B99] mt-2 ${consent ? "invisible" : ""}`} aria-hidden={consent}>Check the box above to continue.</p>

        <p className="text-center text-[14px] text-[#B0AEBC] mt-8">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0071E3] font-medium" data-testid="signup-to-login">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}

const Logo = () => (
  <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
    <defs>
      <linearGradient id="lgsig" x1="0" y1="0" x2="22" y2="22" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0071E3" /><stop offset="1" stopColor="#5856D6" />
      </linearGradient>
    </defs>
    <rect width="22" height="22" rx="6" fill="url(#lgsig)" />
    <path d="M15.5 7.8C14.5 6.7 13 6 11.3 6c-3.2 0-5.3 2.4-5.3 5s2.1 5 5.3 5c1.7 0 3.2-.7 4.2-1.8" stroke="white" strokeWidth="1.8" strokeLinecap="round" fill="none" />
  </svg>
);
